import { http, HttpResponse } from 'msw';

import { MemberStatementCoverage } from 'services/bookkeeping/queries/useStatementCoverage';
import { StatementUploadStatusesResponse } from 'services/bookkeeping/queries/useStatementUploadStatuses';
import type { TransactionEventListResponse } from 'modules/Dashboard/Bookkeeping/Transactions/common/types';

export default {
    debugTransactions: http.post('*/bookkeeping/debug/transactions/', () => HttpResponse.json({})),

    // V2 Transactions
    searchTransactionsV2: http.post('*/bookkeeping/v2/transactions/search/', () =>
        HttpResponse.json({
            count: 0,
            next: null,
            previous: null,
            next_cursor: null,
            previous_cursor: null,
            results: [],
        })
    ),
    transactionsSummaryV2: http.post('*/bookkeeping/v2/transactions/summary/', () =>
        HttpResponse.json({
            total_count: 0,
            total_amount: '0.00',
            facets: [],
        })
    ),
    transactionsByIdsV2: http.get('*/bookkeeping/v2/transactions/', () => HttpResponse.json({ results: [] })),
    transactionEventsV2: http.get('*/bookkeeping/v2/transaction-events/', () =>
        HttpResponse.json<TransactionEventListResponse>({
            count: 0,
            next: null,
            previous: null,
            next_cursor: null,
            previous_cursor: null,
            results: [],
        })
    ),
    updateTransactionV2: http.post('*/bookkeeping/v2/transactions/:transactionId/update/', ({ params }) =>
        HttpResponse.json({ transaction_id: params.transactionId, status: 'synced' })
    ),
    splitTransactionV2: http.post('*/bookkeeping/v2/transactions/:transactionId/split/', ({ params }) =>
        HttpResponse.json({
            transaction_id: params.transactionId,
            status: 'synced',
            gl_account: null,
            error_details: [],
        })
    ),
    transactionGLAccountMaps: http.post('*/bookkeeping/v2/gl-accounts/transaction-maps/', () =>
        HttpResponse.json({ transactions: [] })
    ),
    glAccountsV2: http.get('*/bookkeeping/v2/gl-accounts/', () =>
        HttpResponse.json({
            count: 0,
            next: null,
            previous: null,
            results: [],
        })
    ),
    bankAccountsV2: http.get('*/bookkeeping/v2/bank-accounts/', () =>
        HttpResponse.json({
            count: 0,
            next: null,
            previous: null,
            results: [],
        })
    ),

    // V1 Transactions
    memberTransactions: http.get('*/bookkeeping/member-transactions/', () =>
        HttpResponse.json({
            count: 0,
            next: null,
            previous: null,
            current_cursor: '',
            results: [],
        })
    ),

    // Invoices
    hasSentInvoices: http.get('*/bookkeeping/invoices/has-sent-invoices/', () =>
        HttpResponse.json({ has_sent_invoices: false })
    ),

    // Accounting Onboarding
    // Empty by default: a surface that needs accounts overrides this key with its own fixture.
    statementCoverage: http.get('*/bookkeeping/v2/bank-statements/coverage/', () =>
        HttpResponse.json({
            accounts: [],
            coverage_start: null,
            accounts_unavailable: false,
        } satisfies MemberStatementCoverage)
    ),
    // Typed at the wire, envelope included — the hook unwraps `uploaded_statement_statuses`, so a
    // fixture shaped like the unwrapped map would serve a body without that key and error every
    // query that reads it; `satisfies` on the envelope is what catches the drift here instead.
    statementUploadStatuses: http.get('*/bookkeeping/v2/bank-statements/upload-statuses/', () =>
        HttpResponse.json({ uploaded_statement_statuses: {} } satisfies StatementUploadStatusesResponse)
    ),
    accountingOnboardingProgress: http.get('*/bookkeeping/accounting-onboarding/progress/', () =>
        HttpResponse.json({
            uuid: 'onb-1',
            is_completed: true,
            bank_connection_status: 'completed',
            transaction_import_status: 'completed',
            home_office_setup_status: 'completed',
            other_document_upload_status: 'completed',
        })
    ),

    // The ordinary answer: most transactions carry no AI explanation, and the request layer reads a
    // 404 as "nothing to show". Stories that want the field rendered override this key with a 200.
    aiCategorizationV2: http.get('*/bookkeeping/v2/transactions/:transactionId/ai-categorization/', () =>
        HttpResponse.json(
            { error: 'No AI categorization explanation is available for this transaction.' },
            { status: 404 }
        )
    ),

    // Mutations (no-op for stories)
    updateTransactionGLAccount: http.post('*/bookkeeping/member-transactions/update-gl-account/', () =>
        HttpResponse.json({})
    ),
    submitMemberReview: http.post('*/bookkeeping/member-transactions/submit-member-review/', () =>
        HttpResponse.json({})
    ),
    updateTransactionMemo: http.post('*/bookkeeping/member-transactions/update-memo/', () => HttpResponse.json({})),
};
